export const mapEntityEntriesToTableEntries = (entries) => {
  const headers = [];
  const rows = [];

  entries?.forEach((entry) => {
    headers.push({ name: entry.name, language: entry.language, ...(entry.isCustom && { isCustom: true }) });
    entry.phrases?.forEach((word, index) => {
      if (rows[index]) {
        rows[index].push(word);
      } else {
        rows.push([word]);
      }
    });
  });

  return { headers, rows };
};

export const mapTableEntriesToEntityEntries = (headers, rows) => {
  const transposed = rows[0].map((_, colIndex) => rows.map((row) => row[colIndex]));
  const entries = headers.map((header, index) => ({ ...header, phrases: transposed[index] }));
  return entries;
};

const getBaseLanguage = (language) => language.split('-')[0].trim().toUpperCase();

/**
 * Whether the columns can produce at least one provider glossary. A glossary needs two language columns, and a custom
 * language replaces its parent's column (same code), so a custom column only pairs with regular columns of other
 * languages, e.g. regular DE + custom with parent DE can never build a glossary.
 */
export const canBuildGlossary = (headers) => {
  const regular = headers.filter(({ isCustom }) => !isCustom);
  const custom = headers.filter(({ isCustom }) => isCustom);

  if (regular.length >= 2) return true;

  return custom.some(
    ({ language }) => regular.filter((header) => getBaseLanguage(header.language) !== getBaseLanguage(language)).length,
  );
};

export const downloadAsCSV = (headers, rows, title) => {
  // Step 1 Extract column names
  const headerRow = headers.map((header) => header.name).join(','); // Header row
  // Step 2: Convert rows into CSV format
  const csvRows = rows.map((row) => row.join(','));

  // Step 3: Combine headers and rows
  const csvContent = [headerRow, ...csvRows].join('\n');

  // Step 4: Create a Blob and download the file
  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);

  const a = document.createElement('a');
  a.href = url;
  a.download = title;
  document.body.appendChild(a);
  a.click();

  // Cleanup
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// RFC 4180 style parser: handles quoted fields, escaped quotes, commas and newlines inside quotes
export const parseCSV = (text) => {
  const content = text.replace(/^﻿/, '');
  const records = [];
  let record = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < content.length; i++) {
    const char = content[i];
    if (inQuotes) {
      if (char === '"' && content[i + 1] === '"') {
        field += '"';
        i++;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      record.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && content[i + 1] === '\n') i++;
      record.push(field);
      records.push(record);
      record = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field !== '' || record.length) {
    record.push(field);
    records.push(record);
  }

  return records.filter((r) => r.some((cell) => cell.trim() !== ''));
};

/**
 * Replaces the content of the existing columns with the CSV content, for the languages present in the CSV.
 * Columns are matched by header name (case-insensitive), CSV columns that don't exist in the glossary are ignored.
 * Returns the new rows, or null when no CSV column matched.
 */
export const applyCSVToRows = (csvText, headers, rows) => {
  const [csvHeaders, ...csvRows] = parseCSV(csvText);
  if (!csvHeaders) return null;

  const normalize = (value) => (value || '').trim().toLowerCase();
  const columnMap = []; // [glossaryColIndex, csvColIndex]
  headers.forEach((header, colIndex) => {
    const csvIndex = csvHeaders.findIndex((name) => normalize(name) === normalize(header.name));
    if (csvIndex !== -1) columnMap.push([colIndex, csvIndex]);
  });
  if (!columnMap.length) return null;

  const rowCount = Math.max(rows.length, csvRows.length);
  return Array.from({ length: rowCount }, (_, rowIndex) => {
    const newRow = headers.map((_, colIndex) => rows[rowIndex]?.[colIndex] ?? '');
    columnMap.forEach(([colIndex, csvIndex]) => {
      newRow[colIndex] = csvRows[rowIndex]?.[csvIndex] ?? '';
    });
    return newRow;
  });
};
